import React, { useState } from 'react';
import { ChevronDown, Code, AlertTriangle, Lightbulb, Copy, Check } from 'lucide-react';
import './FailedColumnDetails.css';

function FailedColumnDetails({ failedColumnsAnalysis }) {
  const [expandedDimensions, setExpandedDimensions] = useState({});
  const [copiedIndex, setCopiedIndex] = useState(null);

  const toggleDimension = (dimension) => {
    setExpandedDimensions(prev => ({
      ...prev,
      [dimension]: !prev[dimension]
    }));
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const getSeverityColor = (severity) => {
    const severityMap = {
      'Critical': '#dc2626',
      'High': '#f97316',
      'Medium': '#fbbf24',
      'Low': '#22c55e',
      'Excellent': '#10b981'
    };
    return severityMap[severity] || '#6b7280';
  };

  if (!failedColumnsAnalysis || failedColumnsAnalysis.length === 0) {
    return null;
  }

  return (
    <div className="failed-column-details">
      <div className="details-header mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <AlertTriangle className="w-8 h-8 text-red-500" />
          Detailed Column Failure Analysis
        </h2>
        <p className="text-gray-600 mt-2">
          View detailed information about failed columns including sample errors, root causes, and remediation steps
        </p>
      </div>

      <div className="space-y-4">
        {failedColumnsAnalysis.map((dimensionData, dimIdx) => {
          const isExpanded = expandedDimensions[dimensionData.dimension];
          const failureRate = ((dimensionData.failed / dimensionData.total) * 100).toFixed(2);
          
          return (
            <div key={dimIdx} className="dimension-card bg-white rounded-xl border-2 border-gray-100 shadow-md overflow-hidden">
              {/* Dimension Header */}
              <div
                className="dimension-header bg-gradient-to-r from-blue-50 to-indigo-50 p-4 cursor-pointer hover:bg-gradient-to-r hover:from-blue-100 hover:to-indigo-100 transition-all"
                onClick={() => toggleDimension(dimensionData.dimension)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    <ChevronDown
                      className={`w-5 h-5 text-gray-600 transition-transform ${
                        isExpanded ? 'rotate-180' : ''
                      }`}
                    />
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900">
                        {dimensionData.dimension}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        <span className="font-semibold text-red-600">{dimensionData.failed.toLocaleString()}</span> failed out of{' '}
                        <span className="font-semibold">{dimensionData.total.toLocaleString()}</span> validations
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-2xl font-bold" style={{ color: getSeverityColor(dimensionData.status || 'Medium') }}>
                        {failureRate}%
                      </div>
                      <div className="text-xs font-semibold text-gray-600 uppercase">
                        {dimensionData.status}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="dimension-content p-6 space-y-6 border-t-2 border-gray-100">
                  {dimensionData.failed_columns && dimensionData.failed_columns.map((column, colIdx) => (
                    <div key={colIdx} className="column-failure-card bg-gray-50 rounded-lg p-5 border-l-4" 
                      style={{ borderLeftColor: getSeverityColor(column.status) }}>
                      
                      {/* Column Header */}
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-base font-bold text-gray-900">{column.column}</h4>
                          <span className="px-3 py-1 rounded-full text-xs font-bold text-white"
                            style={{ backgroundColor: getSeverityColor(column.status) }}>
                            {column.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 italic">{column.rule}</p>
                      </div>

                      {/* Statistics */}
                      <div className="grid grid-cols-4 gap-3 mb-4">
                        <div className="bg-white p-3 rounded border border-gray-200 text-center">
                          <div className="text-lg font-bold text-gray-900">{column.passed.toLocaleString()}</div>
                          <div className="text-xs text-gray-600">Passed</div>
                        </div>
                        <div className="bg-white p-3 rounded border border-gray-200 text-center">
                          <div className="text-lg font-bold text-red-600">{column.failed.toLocaleString()}</div>
                          <div className="text-xs text-gray-600">Failed</div>
                        </div>
                        <div className="bg-white p-3 rounded border border-gray-200 text-center">
                          <div className="text-lg font-bold text-gray-900">{column.total_validations.toLocaleString()}</div>
                          <div className="text-xs text-gray-600">Total</div>
                        </div>
                        <div className="bg-white p-3 rounded border border-gray-200 text-center">
                          <div className="text-lg font-bold text-blue-600">{column.success_rate.toFixed(2)}%</div>
                          <div className="text-xs text-gray-600">Rate</div>
                        </div>
                      </div>

                      {/* Sample Failed Details */}
                      {column.failed_sample_details && column.failed_sample_details.length > 0 && (
                        <div className="mb-4">
                          <h5 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-yellow-500" />
                            Sample Failed Records (First 5)
                          </h5>
                          <div className="bg-white rounded border border-gray-200 overflow-hidden">
                            <table className="w-full text-xs">
                              <thead className="bg-gray-100 border-b border-gray-200">
                                <tr>
                                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Row</th>
                                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Value</th>
                                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Reason</th>
                                </tr>
                              </thead>
                              <tbody>
                                {column.failed_sample_details.map((sample, sIdx) => (
                                  <tr key={sIdx} className="border-b border-gray-100 hover:bg-gray-50">
                                    <td className="px-3 py-2 font-mono text-gray-900">{sample.row}</td>
                                    <td className="px-3 py-2 font-mono text-gray-600 truncate max-w-xs" title={sample.value}>
                                      {sample.value}
                                    </td>
                                    <td className="px-3 py-2 text-red-600 font-semibold">{sample.reason}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Root Cause & AI Insight */}
                      {column.ai_insight && (
                        <div className="mb-4 bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                          <div className="flex items-start gap-3">
                            <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <h5 className="font-bold text-gray-900 mb-2">AI Insights & Root Cause Analysis</h5>
                              <p className="text-sm text-gray-700 whitespace-pre-wrap">{column.ai_insight}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Fix Solutions */}
                      <div className="space-y-3">
                        {column.sql_fix && (
                          <div className="fix-card">
                            <div className="flex items-center justify-between mb-2">
                              <h6 className="font-bold text-gray-900 flex items-center gap-2">
                                <Code className="w-4 h-4 text-orange-600" />
                                SQL Fix
                              </h6>
                              <button
                                onClick={() => copyToClipboard(column.sql_fix, `sql-${colIdx}`)}
                                className="flex items-center gap-1 px-3 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50 transition-all"
                              >
                                {copiedIndex === `sql-${colIdx}` ? (
                                  <>
                                    <Check className="w-3 h-3 text-green-600" />
                                    Copied
                                  </>
                                ) : (
                                  <>
                                    <Copy className="w-3 h-3 text-gray-600" />
                                    Copy
                                  </>
                                )}
                              </button>
                            </div>
                            <pre className="bg-gray-900 text-gray-100 p-3 rounded text-xs overflow-x-auto">
{column.sql_fix}
                            </pre>
                          </div>
                        )}

                        {column.python_fix && (
                          <div className="fix-card">
                            <div className="flex items-center justify-between mb-2">
                              <h6 className="font-bold text-gray-900 flex items-center gap-2">
                                <Code className="w-4 h-4 text-blue-600" />
                                Python Fix
                              </h6>
                              <button
                                onClick={() => copyToClipboard(column.python_fix, `python-${colIdx}`)}
                                className="flex items-center gap-1 px-3 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50 transition-all"
                              >
                                {copiedIndex === `python-${colIdx}` ? (
                                  <>
                                    <Check className="w-3 h-3 text-green-600" />
                                    Copied
                                  </>
                                ) : (
                                  <>
                                    <Copy className="w-3 h-3 text-gray-600" />
                                    Copy
                                  </>
                                )}
                              </button>
                            </div>
                            <pre className="bg-gray-900 text-gray-100 p-3 rounded text-xs overflow-x-auto">
{column.python_fix}
                            </pre>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default FailedColumnDetails;
